import { getPairMaxPayoutPercent } from "@/types/index";
import type { PairMessage } from "@cronika/proto/common/trading_pb";
import { useTranslation } from "@cronika/locales";
import { type FC, useMemo, useState } from "react";
import type { TAddCategory } from "../types";
import { CATEGORY_ALL, PAIR_CATEGORY } from "../types";
import { PairRow } from "./PairRow";

type TPairListProps = {
  pairs: PairMessage[];
  selectedCategory: TAddCategory;
  activePair: PairMessage | null;
  selectedPairs: PairMessage[];
  favoritePairs?: PairMessage[];
  onToggleFavorite?: (pair: PairMessage) => void;
  selectPairAsActive?: (pairId: string) => void;
  onPairSelect: (pair: PairMessage) => void;
  onRemovePair: (pairId: string) => void;
  searchQuery?: string;
};

type SortDirection = "none" | "asc" | "desc";

export const GRID_COLS = "grid grid-cols-[20px_36px_1fr_52px] items-center";

export const PairList: FC<TPairListProps> = ({
  pairs,
  selectedCategory,
  activePair,
  selectedPairs,
  favoritePairs = [],
  onToggleFavorite,
  onPairSelect,
  searchQuery = "",
}) => {
  const { t } = useTranslation("pairs-control");
  const [sortDirection, setSortDirection] = useState<SortDirection>("none");

  const selectedIds = new Set(selectedPairs.map((p) => p.id));
  const favoriteIds = new Set(favoritePairs.map((p) => p.id));
  const query = searchQuery.toLowerCase().trim();

  const sortedPairs = useMemo(() => {
    const pool =
      query && selectedCategory !== PAIR_CATEGORY.FAVORITE
        ? pairs
        : selectedCategory === PAIR_CATEGORY.FAVORITE
          ? pairs.filter((pair) => favoriteIds.has(pair.id))
          : selectedCategory === CATEGORY_ALL
            ? pairs
            : pairs.filter((pair) => pair.type === selectedCategory);

    const filtered = pool.filter(
      (pair) =>
        !query ||
        pair.symbol.toLowerCase().includes(query) ||
        pair.baseAsset.toLowerCase().includes(query) ||
        pair.quoteAsset.toLowerCase().includes(query),
    );

    return [...filtered].sort((a, b) => {
      const aActive = a.isTradingEnabled === false ? 1 : 0;
      const bActive = b.isTradingEnabled === false ? 1 : 0;
      if (aActive !== bActive) return aActive - bActive;
      if (sortDirection === "none") return 0;
      const aVal = getPairMaxPayoutPercent(a);
      const bVal = getPairMaxPayoutPercent(b);
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [pairs, selectedCategory, favoriteIds, query, sortDirection]);

  const cycleSortDirection = () => {
    setSortDirection((prev) => {
      if (prev === "none") return "desc";
      if (prev === "desc") return "asc";
      return "none";
    });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden px-3.5">
      {/* Column headers */}
      <div className={`${GRID_COLS} pb-1.5 text-[11px] text-[#8C8C8C]`}>
        <span />
        <span className="col-span-2 ml-[3px]">{t("pairs-control.asset")}</span>
        <button type="button" onClick={cycleSortDirection} className="flex justify-end cursor-pointer">
          <span className="flex items-center gap-1 pr-3">
            {t("pairs-control.payout")}
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none" className="shrink-0">
              <path d="M5 2L7 4.5H3L5 2Z" fill={sortDirection === "asc" ? "#F5A524" : "#8C8C8C"} />
              <path d="M5 8L3 5.5H7L5 8Z" fill={sortDirection === "desc" ? "#F5A524" : "#8C8C8C"} />
            </svg>
          </span>
        </button>
      </div>

      {/* Pair rows */}
      <div className="custom-scroll flex-1 overflow-y-auto">
        {sortedPairs.map((pair) => (
          <PairRow
            key={pair.id}
            pair={pair}
            gridCols={GRID_COLS}
            isFavorite={favoriteIds.has(pair.id)}
            isSelected={selectedIds.has(pair.id)}
            isActivePair={activePair?.id === pair.id}
            onSelect={onPairSelect}
            onToggleFavorite={onToggleFavorite}
          />
        ))}
      </div>
    </div>
  );
};
